// The Lumi glyph — the "lu" under five rays — as the drawing of the `lumi` meaning
// (ADR 0096), in place of a Heroicons glyph. The paths are the 32 px favicon source,
// already square and hinted for small sizes, so it reads at 14 px in the sidebar foot.
//
// Why a functional component with `attrs` spread rather than a `.vue` file: LumiIcon
// calls a vocabulary entry as a function and clones the vnode it returns with the
// caller's attributes, exactly as it does for a Heroicons glyph; this keeps the same
// shape, so every `name="lumi"` / `LUMI_ICONS['lumi']` call site stays as it was.
//
// The glyph is the BOLD cut: every shape is filled and traced with a 0.5-unit stroke
// in its own colour, exactly as the bold favicon source draws it. The stroke is set on
// the shapes themselves, so a caller's `stroke-width` on the <svg> (callers pass one
// for the outline glyphs) is overridden and cannot distort it. `currentColor` lets the
// surrounding text colour paint it. A screen that shows the glyph big, as artwork
// rather than as an icon, keeps the light cut by switching the stroke off
// (workspace-ui's ForkHero.vue).
//
// THE ORDER OF LUMI_GLYPH_PATHS IS A CONTRACT. Counted from 1 (as `:nth-child` counts), the rays are
// path 1 (left bar), 6 (left diagonal), 7 (vertical), 5 (right diagonal) and 4 (right
// bar); 2 and 3 are the letters, the dot is the circle after them. workspace-ui's
// ForkHero.vue fans the rays in by exactly those positions, and ForkHero.spec.ts pins
// them — reorder LUMI_GLYPH_PATHS and both have to move with it. LumiChatGlyph.vue (the sidebar's
// lumi button) reads the same positions to light the rays one by one.
//
// portal-ui vendors this file byte-identical (it cannot use the shared component library) —
// scripts/check-duplicate-copies.mjs pins it; edit component-lib's, then copy.
import { h } from 'vue';
import type { FunctionalComponent } from 'vue';

export const LUMI_GLYPH_PATHS: readonly string[] = [
  'M6.74,9.82v.27c0,.54.45.98,1.07.98h4.06c.63,0,1.07-.45,1.07-.98v-.27c0-.54-.45-.98-1.07-.98h-4.06c-.63,0-1.07.45-1.07.98Z',
  'M19.25,14.54c0-.69.5-1.19,1.09-1.19h.3c.6,0,1.09.5,1.09,1.19v12.99c0,1.88-1.49,3.37-3.37,3.37h-.4c-1.88,0-3.37-1.49-3.37-3.37v-12.99c0-.69.5-1.19,1.09-1.19h.3c.6,0,1.09.5,1.09,1.19v12.89c0,.69.4,1.19,1.09,1.19s1.09-.5,1.09-1.19v-12.89Z',
  'M12.35,28.21v-13.67c0-.69-.5-1.19-1.09-1.19h-.3c-.59,0-1.09.5-1.09,1.19v14.97c0,.66.5,1.17.95,1.17h2.65s-1.04-.66-1.12-2.48Z',
  'M18.83,9.9v.27c0,.54.45.98,1.07.98h4.06c.63,0,1.07-.45,1.07-.98v-.27c0-.54-.45-.98-1.07-.98h-4.06c-.63,0-1.07.45-1.07.98Z',
  'M17.91,7.47l.19.19c.38.38,1.01.38,1.45-.06l2.87-2.87c.44-.44.44-1.08.06-1.45l-.19-.19c-.38-.38-1.01-.38-1.45.06l-2.87,2.87c-.44.44-.44,1.08-.06,1.45Z',
  'M9.21,3.36l-.19.19c-.38.38-.38,1.01.06,1.45l2.87,2.87c.44.44,1.08.44,1.45.06l.19-.19c.38-.38.38-1.01-.06-1.45l-2.87-2.87c-.44-.44-1.08-.44-1.45-.06Z',
  'M15.67,7.35h.27c.54,0,.98-.45.98-1.07V2.22c0-.63-.45-1.07-.98-1.07h-.27c-.54,0-.98.45-.98,1.07v4.06c0,.63.45,1.07.98,1.07Z',
];

/** The dot over the letters, drawn after the paths. */
export const LUMI_GLYPH_DOT = { cx: 15.81, cy: 10.09, r: 1.19 } as const;

const BOLD = { stroke: 'currentColor', 'stroke-width': 0.5, 'stroke-miterlimit': 10 };

export const LumiGlyphIcon: FunctionalComponent = (_props, { attrs }) =>
  h('svg', { xmlns: 'http://www.w3.org/2000/svg', viewBox: '0 0 32 32', fill: 'currentColor', 'aria-hidden': 'true', ...attrs }, [
    ...LUMI_GLYPH_PATHS.map((d) => h('path', { d, ...BOLD })),
    h('circle', { ...LUMI_GLYPH_DOT, ...BOLD }),
  ]);
